import React, { useContext, useEffect, useState } from 'react';
import {
    convertFromRaw,
    convertToRaw,
    EditorState
} from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import { questionMCQAnswerTypeConstants, questionTypeConstants } from '../../helper/constants';
import { Box, Modal, Typography, Button, Divider, Radio, FormControlLabel, Checkbox, Backdrop, CircularProgress, TextField, Tooltip } from '@mui/material';
import { libraryService } from '../../service/question/libraryService';
import { sweetStatusAlert } from '../../components/common/SweetAlert';
import { MainContext } from '../../context/main/main-context';
import { namespace } from '../../helper/namespace';
import CustomEditor from '../../components/common/editor/CustomEditor';

function EditQuestionModal({ open, setOpen, selectedQuestionId, selectedQuestion, currentAssessmentId }) {

    const {fetchLoadAssesmentQuestionById} = useContext(MainContext)
    const [questionState, setQuestionState] = useState(EditorState.createEmpty());
    const [answerState, setAnswerState] = useState(EditorState.createEmpty());
    const [guidanceState, setGuidanceState] = useState(EditorState.createEmpty());
    const [answerStates, setAnswerStates] = useState([]);
    const [subQuestions, setSubQuestions] = useState({});
    const [questionType, setQuestionType] = useState('');
    const [mcqAnswer, setMcqAnswer] = useState('');
    const [isQuestionUpdating, setIsQuestionUpdating] = useState(false);
    const [markAllocation, setMarkAllocation] = useState(0.0);
    const [errors, setErrors] = useState({
        mainMark: "",
        subQuestionMarks: {} 
    });


    useEffect(() => {
        if (selectedQuestion) {            
            
            setQuestionType(selectedQuestion.type);
            
            if (selectedQuestion.questionState) {
                setQuestionState(selectedQuestion.questionState);
            }
            if (selectedQuestion.guidanceState) {
                setGuidanceState(selectedQuestion.guidanceState);
            }
            if (selectedQuestion.mark_allocation) {
                setMarkAllocation(selectedQuestion.mark_allocation);
            }

            if (selectedQuestion.type === questionTypeConstants.MCQ.name) {
                if (selectedQuestion.answerStates) {
                    setAnswerStates(selectedQuestion.answerStates);
                }
                if (selectedQuestion.mcqAnswer) {
                    setMcqAnswer(selectedQuestion.mcqAnswer);
                }
            } else if (selectedQuestion.type === questionTypeConstants.DESCRIPTIVE.name) {
                if (selectedQuestion.answerState) {
                    setAnswerState(selectedQuestion.answerState);
                }
            } else if (selectedQuestion.type === questionTypeConstants.SUBQUESTION.name) {
                if (selectedQuestion.subQuestions) {
                    setSubQuestions(selectedQuestion.subQuestions);
                }
            }
        }
    }, [selectedQuestion]);

    const handleSave = async() => {

        
        if (errors.mainMark) {
            sweetStatusAlert("Error", "Please fix errors in mark allocation before saving", "error");
            return;
        }

        
        const hasSubQuestionErrors = Object.values(errors.subQuestionMarks).some(error => error);
        if (hasSubQuestionErrors) {
            sweetStatusAlert("Error", "Please fix errors in sub-question mark allocations before saving", "error");
            return;
        }
        
        setIsQuestionUpdating(true)

        const questionContent = convertToRaw(questionState.getCurrentContent());

        const guidanceContent = convertToRaw(guidanceState.getCurrentContent());

        let answerContent = null

        const updatedData = {
            question_id: selectedQuestionId,
            qtype: null,
            guidence:[guidanceContent],
            mark_allocation:parseFloat(markAllocation)
        };

        if (questionType === questionTypeConstants.DESCRIPTIVE.name) {
            updatedData.question_content = [questionContent];
            answerContent = convertToRaw(answerState.getCurrentContent());
            updatedData.answer_content = [answerContent];
            updatedData.qtype = questionTypeConstants.DESCRIPTIVE.id
        } 
        else if (questionType === questionTypeConstants.MCQ.name) {
            
            const optionsContent = answerStates.map(state => 
                convertToRaw(state.getCurrentContent())
            );
            
            updatedData.question_content = [questionContent, ...optionsContent];
            updatedData.answer_content = mcqAnswer;
            updatedData.qtype = questionTypeConstants.MCQ.id;
        }
        else if (questionType === questionTypeConstants.SUBQUESTION.name) {


            updatedData.question_content = [questionContent];
            updatedData.qtype = questionTypeConstants.SUBQUESTION.id
            
            if (subQuestions) {
                const processedSubQuestions = {};
                
                Object.entries(subQuestions).forEach(([key, subQ]) => {
                    processedSubQuestions[key] = {
                        ...subQ, 
                        tempQuestion: [convertToRaw(subQ.tempQuestion.getCurrentContent())],
                        tempAnswer: [convertToRaw(subQ.tempAnswer.getCurrentContent())],
                    };
                });
                
                updatedData.subQuestions = processedSubQuestions;
            }
    
        }        

        try {

            const response = await libraryService.updateSingleQuestion(updatedData,currentAssessmentId)

            if(response.success){

                const fetchLoadAssesmentQuestion =  await fetchLoadAssesmentQuestionById?.(currentAssessmentId)
                await Promise.all([fetchLoadAssesmentQuestion])
                    .then((response) => {                        
                        setIsQuestionUpdating(false);
                        setOpen({isOpen: false});
                        sweetStatusAlert("Success","Successfully update the question","success")
                    })
                    .catch((error) => {
                        setIsQuestionUpdating(false)
                        setOpen({isOpen: false});
                        sweetStatusAlert("Success","Successfully update the question,Please refresh screen","success")
                    });
                setErrors({
                    mainMark: "",
                    subQuestionMarks: {}
                });
                
                
            }
            else{
                setIsQuestionUpdating(false)
                sweetStatusAlert("Failed","Failed to update question","error")
                setErrors({
                    mainMark: "",
                    subQuestionMarks: {}
                });
            }
            
        } catch (error) {
            setIsQuestionUpdating(false)
            sweetStatusAlert("Failed","Failed to update question","error")
            setErrors({
                mainMark: "",
                subQuestionMarks: {}
            });
        }

    };

    const handleMcqSingleAnswerChange = (index) => {
        const newAnswers = answerStates.map((_, i) => (i === index ? 100 : 0));
        setMcqAnswer(newAnswers.join(','));
    };

    const handleMcqMultipleAnswerChange = (index) => {
        const currentAnswers = mcqAnswer.split(',').map(val => parseFloat(val.trim()));
        const newAnswers = [...currentAnswers];
        
        // Toggle between 100 and 0 for the selected option
        newAnswers[index] = newAnswers[index] > 0 ? 0 : 100;
        
        // If all are unselected, select the current one
        if (newAnswers.every(val => val === 0)) {
            newAnswers[index] = 100;
        }
        
        // Distribute marks equally if multiple options are selected
        const selectedCount = newAnswers.filter(val => val > 0).length;
        if (selectedCount > 1) {
            const equalMark = 100 / selectedCount;
            newAnswers.forEach((val, i) => {
                if (val > 0) newAnswers[i] = equalMark;
            });
        }
        
        setMcqAnswer(newAnswers.map(val => Math.round(val)).join(','));
    };

    const handleMCQOptionChange = (index, newState) => {
        const newOptions = [...answerStates];
        newOptions[index] = newState;
        setAnswerStates(newOptions);
    };

    const handleMarkChange = (value) => {
        if (isNaN(value) || value <= 0) {
            setErrors(prev => ({
            ...prev,
            mainMark: "Marks must be greater than 0"
            }));
            setMarkAllocation(0.0);
        } else {
            setErrors(prev => ({
            ...prev,
            mainMark: ""
            }));
            setMarkAllocation(value);
        }
    };

    const handleSubQuestionChange = (key, field, newState) => {
        if (field === 'mark_allocation') {
            if (isNaN(newState) || newState <= 0) {
            setErrors(prev => ({
                ...prev,
                subQuestionMarks: {
                ...prev.subQuestionMarks,
                [key]: "Marks must be greater than 0"
                }
            }));
            setSubQuestions(prev => ({
                ...prev,
                [key]: {
                ...prev[key],
                [field]: 0
                }
            }));
            } else {
            setErrors(prev => ({
                ...prev,
                subQuestionMarks: {
                ...prev.subQuestionMarks,
                [key]: ""
                }
            }));
            setSubQuestions(prev => ({
                ...prev,
                [key]: {
                ...prev[key],
                [field]: newState
                }
            }));
            }
        } else {
            setSubQuestions(prev => ({
            ...prev,
            [key]: {
                ...prev[key],
                [field]: newState
            }
            }));
        }
    };

    function convertFileToBase64(file) {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
  
          reader.onload = () => {
            resolve(reader.result);
          };
  
          reader.onerror = (error) => {
            reject(error);
          };
  
          reader.readAsDataURL(file);
        });
    }

    const handleFileUpload = (file) => {
          return new Promise(async (resolve, reject) => {
            try{
              const fileSizeLimit = 10 * 1024 * 1024; // 10MB limit (adjust the value as needed)
              if (file.size > fileSizeLimit) {
                reject(new Error('File size exceeds the limit.'));
                window.Swal.fire({
                  title: namespace.FAILED,
                  text: namespace.FILE_SIZE_IS_EXCEEDED,
                  icon: 'error',
                //   customClass: {
                //     confirmButton: `btn font-weight-bold ${confirmClass}`,
                //   },
                //   onOpen: (modalElement) => {
                //       if (customer === customerConstants.EXO_CUSTOMER2) {
                //           modalElement.querySelector('.swal2-confirm').setAttribute('id', 'confirmButtonId');
                //       }
                //   }
                });
                return;
              }
              const base64Data = await convertFileToBase64(file);
              resolve({ data: { link: base64Data } });   
            }
            catch(error) {
              reject(error);
            }
          });         
    }

    const renderAnswerSection = () => {

        switch (questionType) {

            case questionTypeConstants.DESCRIPTIVE.name:
                return (
                    <>
                        <Typography variant="h6" sx={{ mt: 3 }}>Sample Answer</Typography>

                        <CustomEditor
                            editorState={answerState}
                            onEditorStateChange={setAnswerState}
                            value={answerState}
                        />

                        <TextField 
                            type='number'
                            value={parseFloat(markAllocation) || 0.0} 
                            onChange={(e)=>handleMarkChange(parseFloat(e.target.value))}
                            label="Allocated Marks" 
                            variant="filled" 
                            color="success" 
                            error={!!errors.mainMark}
                            helperText={errors.mainMark}
                        />
                    </>
                );

            case questionTypeConstants.MCQ.name:
                return (
                    <>
                        <Typography variant="h6" sx={{ mt: 3 }}>Options</Typography>
                        {answerStates.map((optionState, index) => (
                            <div key={index} style={{ marginBottom: '15px' }}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    {selectedQuestion.answerTypeId === questionMCQAnswerTypeConstants.One.id ? (
                                        <Radio 
                                            checked={mcqAnswer && parseFloat(mcqAnswer.split(",")[index]?.trim()) > 0}
                                            onChange={() => handleMcqSingleAnswerChange(index)}
                                            sx={{ mr: 1 }} 
                                        />
                                    ) : (
                                        <Checkbox 
                                            checked={mcqAnswer && parseFloat(mcqAnswer.split(",")[index]?.trim()) > 0}
                                            onChange={() => handleMcqMultipleAnswerChange(index)}
                                            sx={{ mr: 1 }} 
                                        />
                                    )}
                                    <CustomEditor
                                        editorState={optionState}
                                        onEditorStateChange={(newState) => handleMCQOptionChange(index, newState)}
                                        value={optionState}
                                    />
                                    
                                </div>
                            </div>
                        ))}

                        <TextField 
                            type='number'
                            value={parseFloat(markAllocation) || 0.0} 
                            onChange={(e)=>handleMarkChange(parseFloat(e.target.value))}
                            label="Allocated Marks" 
                            variant="filled" 
                            color="success" 
                            error={!!errors.mainMark}
                            helperText={errors.mainMark}
                        />
                    </>
                );

            case questionTypeConstants.SUBQUESTION.name:
                return (
                    <>
                        <Typography variant="h6" sx={{ mt: 3 }}>Sub Questions</Typography>
                        {Object.entries(subQuestions).map(([key, subQ], index) => (
                            <div key={key} style={{ 
                                marginBottom: '20px', 
                                padding: '15px', 
                                border: '1px solid #eee', 
                                borderRadius: '4px',
                                backgroundColor: '#f9f9f9'
                            }}>
                                <Typography variant="subtitle1">Sub-question {index + 1}</Typography>

                                <CustomEditor
                                    editorState={subQ.tempQuestion}
                                    onEditorStateChange={(newState) => handleSubQuestionChange(key, 'tempQuestion', newState)}
                                    value={subQ.tempQuestion}
                                />
                                
                                

                                <Typography variant="subtitle2" sx={{ mt: 2 }}>Answer</Typography>

                                <CustomEditor
                                    editorState={subQ.tempAnswer}
                                    onEditorStateChange={(newState) => handleSubQuestionChange(key, 'tempAnswer', newState)}
                                    value={subQ.tempAnswer}
                                />

                                

                                <TextField 
                                    type='number' 
                                    value={parseFloat(subQ.mark_allocation) || 0.0} 
                                    onChange={(e)=>handleSubQuestionChange(key, 'mark_allocation', parseFloat(e.target.value))}
                                    label="Allocated Marks" 
                                    variant="filled" 
                                    color="success" 
                                    error={!!errors.subQuestionMarks[key]}
                                    helperText={errors.subQuestionMarks[key]}
                                />
                            </div>
                        ))}
                    </>
                );

            default:
                return null;
        }
    };

    const handleClose = () => {
        setOpen({isOpen: false});
        setErrors({
            mainMark: "",
            subQuestionMarks: {}
        });
    };

    return (

        <Modal open={open} onClose={() => setOpen({isOpen:false})}>

            <div>

                <Backdrop
                    sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })}
                    open={isQuestionUpdating}
                >
                    <CircularProgress color="inherit" />
                </Backdrop>

                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '90%', md: '70%', lg: '60%' },
                    maxHeight: '90vh',
                    overflowY: 'auto',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: '8px',
                }}>
                    <Typography variant="h5" gutterBottom>
                        Edit Question {selectedQuestionId}
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="h6">Question</Typography>

                    <CustomEditor
                        editorState={questionState}
                        onEditorStateChange={setQuestionState}
                        value={questionState}
                    />

                    

                    {renderAnswerSection()}

                    <Divider sx={{ my: 3 }} />

                    <Typography variant="h6">Guidance</Typography>

                    <CustomEditor
                        editorState={guidanceState}
                        onEditorStateChange={setGuidanceState}
                        value={guidanceState}
                    />

                    

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                        <Button variant="outlined" onClick={handleClose}>
                            Cancel
                        </Button>
                        {/* <Button 
                            variant="contained" 
                            onClick={handleSave}
                            color="primary"
                        >
                            Save Changes
                        </Button> */}

                        <Tooltip 
                            placement="top"
                            title={
                                errors.mainMark ? "Fix main question mark error" :
                                Object.values(errors.subQuestionMarks).some(error => error) ? "Fix sub-question mark errors" :
                                markAllocation <= 0 ? "Main question marks must be > 0" :
                                (questionType === questionTypeConstants.SUBQUESTION.name && 
                                Object.values(subQuestions).some(subQ => subQ.mark_allocation <= 0)) ? 
                                "All sub-question marks must be > 0" : ""
                            }
                            >
                            <span> {/* Wrap the button in a span for the tooltip to work when disabled */}
                                <Button 
                                variant="contained" 
                                onClick={handleSave}
                                color="primary"
                                disabled={!!errors.mainMark || Object.values(errors.subQuestionMarks).some(error => error)}
                                >
                                Save Changes
                                </Button>
                            </span>
                            </Tooltip>
                    </Box>
                </Box>
            </div>
        </Modal>
    );
}

export default EditQuestionModal;